import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, input, OnInit, signal, ViewChild, viewChild } from '@angular/core';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { Icons } from '../../util/Icons';
import { UntypedFormControl } from '@angular/forms';
import { RealEstateService } from '@eadsysteme/ewsrealestateclient-v2';
import { TableColumn } from '../../../@vex/interfaces/table-column.interface';
import { fadeInUp400ms } from '../../../@vex/animations/fade-in-up.animation';
import { stagger40ms } from '../../../@vex/animations/stagger.animation';
import { SelectionModel } from '@angular/cdk/collections';
import { SessionService } from '../../services/session.service';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { TranslocoLocaleService } from '@jsverse/transloco-locale';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import {
	Address,
	ConsumptionRequest,
	EedPrintService,
	MassPrintData,
	RealEstatePrintState,
	RealEstateReport,
	RealEstateSetting,
} from '@eadsysteme/energyefficiencyclient-v1';
import { SnackbarService } from '../../services/snackbar.service';
import { LayoutService } from '../../../@vex/services/layout.service';
import { finalize } from 'rxjs/operators';
import { JobStateService } from '../../../@vex/services/jobState.service';
import { MassPrintingFilterDialogComponent, PrintOptionEnum } from './filter-dialog/mass-printing-filter-dialog.component';
import { JobLogService } from '@eadsysteme/joblogclient-v1';
import { catchError, defaultIfEmpty, firstValueFrom, Observable, of, zip } from 'rxjs';
import { PrintDialogComponent } from './print-dialog/print-dialog.component';
import { BreadCrumbService } from '../../../@vex/components/breadcrumbs/BreadCrumbService';
import { Router } from '@angular/router';
import { PermissionObject } from '@eadsysteme/ewsauthclient-v1';
import { PropertyManagementRightsService } from '../tenant-unit-detail-legacy/roles-view/property-management-rights.service';
import dayjs from 'dayjs';
import { DayjsDateAdapter, MAT_DAYJS_DATE_ADAPTER_OPTIONS, MAT_DAYJS_MONTHYEAR_FORMATS } from '../../util/material-dayjs-adapter';
import utc from 'dayjs/plugin/utc';
import { MatSlideToggle, MatSlideToggleChange } from '@angular/material/slide-toggle';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldDefaultOptions } from '@angular/material/form-field';
import {
	MatCell,
	MatCellDef,
	MatColumnDef,
	MatHeaderCell,
	MatHeaderCellDef,
	MatHeaderRow,
	MatHeaderRowDef,
	MatNoDataRow,
	MatRow,
	MatRowDef,
	MatTable,
	MatTableDataSource,
} from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { AraPaginatorComponent } from '../util/ara-paginator/ara-paginator.component';
import { PageLayoutComponent } from '../../../@vex/components/page-layout/page-layout.component';
import { PageLayoutHeaderDirective } from '../../../@vex/components/page-layout/page-layout-header.directive';
import { BreadcrumbsComponent } from '../../../@vex/components/breadcrumbs/breadcrumbs.component';
import { PageLayoutContentDirective } from '../../../@vex/components/page-layout/page-layout-content.directive';
import { MatIcon } from '@angular/material/icon';
import { MatInput, MatLabel } from '@angular/material/input';
import { MatButton } from '@angular/material/button';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatTooltip } from '@angular/material/tooltip';
import { DateUtil } from '../../util/DateUtil';
import ManualDeliveryOptionEnum = RealEstateSetting.ManualDeliveryOptionEnum;

dayjs.extend(utc);

@Component({
	selector: 'vex-mass-printing',
	templateUrl: './mass-printing.component.html',
	styleUrls: ['./mass-printing.component.scss'],
	animations: [fadeInUp400ms, stagger40ms],
	providers: [
		{
			provide: DateAdapter,
			useClass: DayjsDateAdapter,
			deps: [MAT_DATE_LOCALE, MAT_DAYJS_DATE_ADAPTER_OPTIONS],
		},
		{
			provide: MAT_DATE_FORMATS,
			useValue: MAT_DAYJS_MONTHYEAR_FORMATS,
		},
		{
			provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
			useValue: {
				appearance: 'outline',
			} as MatFormFieldDefaultOptions,
		},
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [
		PageLayoutComponent,
		TranslocoDirective,
		PageLayoutHeaderDirective,
		BreadcrumbsComponent,
		PageLayoutContentDirective,
		MatIcon,
		MatInput,
		MatLabel,
		MatButton,
		MatSlideToggle,
		MatProgressSpinner,
		MatTable,
		MatSort,
		MatColumnDef,
		MatHeaderCellDef,
		MatHeaderCell,
		MatCheckbox,
		MatCellDef,
		MatCell,
		MatSortHeader,
		MatHeaderRowDef,
		MatHeaderRow,
		MatRowDef,
		MatRow,
		MatNoDataRow,
		AraPaginatorComponent,
		MatTooltip,
	],
})
export class MassPrintingComponent implements OnInit {
	private translocoService = inject(TranslocoService);
	private translocoLocaleService = inject(TranslocoLocaleService);
	private realEstateService = inject(RealEstateService);
	private snackBar = inject(SnackbarService);
	private dialog = inject(MatDialog);
	private eedPrintController = inject(EedPrintService);
	layoutService = inject(LayoutService);
	private dateUtil = inject(DateUtil);
	sessionService = inject(SessionService);
	icons = inject(Icons);
	private jobStateService = inject(JobStateService);
	private jobLogService = inject(JobLogService);
	breadCrumbService = inject(BreadCrumbService);
	private readonly propertyManagementRightsService = inject(PropertyManagementRightsService);
	private readonly router = inject(Router);

	private destroyRef = inject(DestroyRef);

	date = new UntypedFormControl(dayjs().utcOffset(0, true));
	currentDate = dayjs().utcOffset(0, true);
	model: any = {
		showNotPrinted: true,
	};
	printOptions: string[] = Object.values(PrintOptionEnum);
	manualDeliveryOptions: string[] = Object.values(RealEstateSetting.ManualDeliveryOptionEnum.UNKNOWN);
	deliveryToResident: ManualDeliveryOptionEnum = RealEstateSetting.ManualDeliveryOptionEnum.DELIVERY_TO_RESIDENT;
	deliveryToOwner: ManualDeliveryOptionEnum = RealEstateSetting.ManualDeliveryOptionEnum.DELIVERY_TO_OWNER;
	deliveryToPM: ManualDeliveryOptionEnum = RealEstateSetting.ManualDeliveryOptionEnum.DELIVERY_TO_PM;
	dateSelect: Date[] = [];
	readonly sort = viewChild(MatSort);
	readonly araPaginator = viewChild(AraPaginatorComponent);
	@ViewChild(MatPaginator) paginator: MatPaginator;
	pageSizeOptions: number[] = [10, 20, 50, 100];
	dataSource: MatTableDataSource<RealEstateDatasource>;
	selection = new SelectionModel<RealEstateDatasource>(true, []);

	showAggregated = true;

	readonly columns = input<TableColumn<RealEstateDatasource>[]>([
		{ label: this.translate('realestates.selection'), property: 'selection', type: 'text', visible: true },
		{ label: this.translate('realestates.number'), property: 'number', type: 'text', visible: true },
		{
			label: this.translate('massPrint.tenantUnitNumber'),
			property: 'tenantUnitNumber',
			type: 'text',
			visible: true,
		},
		{
			label: this.translate('realestates.propertyManagementNr'),
			property: 'propertyManagementNumber',
			type: 'text',
			visible: true,
		},
		{
			label: this.translate('realestates.propertyManagementAccountNr'),
			property: 'propertyManagementAccountNumber',
			type: 'text',
			visible: true,
		},
		{ label: this.translate('user.address'), property: 'addressBeautified', type: 'text', visible: true },
		{
			label: this.translate('massPrint.tenantUnitCounter'),
			property: 'tenantUnitCounter',
			type: 'text',
			visible: true,
		},
	]);
	locale: string;
	finishedLoading = true;
	filtersSet = false;
	isMobile = false;
	tenantUnitCounter = 0;
	currentArea: number;
	private filters: any = {};
	uvisInQueue = signal(0);

	constructor() {
		const layoutService = this.layoutService;

		this.isMobile = layoutService.isMobile();
		this.locale = this.translocoLocaleService.getLocale();
		for (let i = 0; i <= 12; i++) {
			this.dateSelect.push(this.currentDate.set('day', 15).subtract(i, 'month').toDate());
		}

		this.selection.changed.subscribe((selectedRow) => {
			selectedRow.added.forEach((realEstateRow) => {
				this.tenantUnitCounter += realEstateRow.tenantUnits?.length;
			});
			selectedRow.removed.forEach((realEstateRow) => {
				this.tenantUnitCounter -= realEstateRow.tenantUnits?.length;
			});
		});
	}

	get visibleColumns() {
		return this.columns()
			.filter((column) => column.visible)
			.map((column) => column.property);
	}

	ngOnInit(): void {
		this.currentArea = this.sessionService.effectiveArea;
		this.setBreadcrumb();

		this.manualDeliveryOptions = Object.values([RealEstateSetting.ManualDeliveryOptionEnum.PROCESSING_PM]);
		this.model.manualDeliveryOption = this.filters.manualDeliveryOption = RealEstateSetting.ManualDeliveryOptionEnum.PROCESSING_PM;
		this.printOptions = Object.values([PrintOptionEnum.NOT_PRINTED]);

		// Der Filter-Dialog blockiert die Seite (disableClose), darum erst oeffnen, wenn das
		// HV-Recht aufgeloest ist - sonst sitzt der Nutzer in einem Dialog ohne Ausweg.
		this.propertyManagementRightsService
			.resolve()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((isPropertyManagement) => {
				if (!isPropertyManagement) {
					this.snackBar.error(this.translocoService.translate('errors.massPrintNoPropertyManagement'));
					this.router.navigate(['/dashboard']);
					return;
				}
				this.filters = {
					printOption: 'NOT_PRINTED',
					manualDelivery: 'PROCESSING_PM',
				};
				this.model.printOption = this.filters.printOption = PrintOptionEnum.NOT_PRINTED;
				this.openFilterDialog();
			});
	}

	openFilterDialog() {
		const filterDialogData: any = {};
		filterDialogData.filters = this.filters;
		filterDialogData.printOptions = this.printOptions;
		filterDialogData.manualDeliveryOptions = this.manualDeliveryOptions;
		filterDialogData.dateOptions = this.dateSelect;
		const dialogRef = this.dialog.open(MassPrintingFilterDialogComponent, {
			width: '550px',
			data: filterDialogData,
			disableClose: true,
			autoFocus: false,
			restoreFocus: false,
		});
		dialogRef
			.afterClosed()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (columnFiltersFromDialog) => {
					if (columnFiltersFromDialog?.saved) {
						this.filters = columnFiltersFromDialog.columnFilters;
						this.model.dateSelect = this.filters.date;
						this.model.manualDeliveryOption = this.filters.manualDeliveryOption;
						this.model.printOption = this.filters.printOption;
						this.setFilterState(true);
						this.reloadData();
					}
				},
			});
	}

	translate(key: string): string {
		return this.translocoService.translate(key);
	}

	applyFilter(filterValue: string) {
		filterValue = filterValue.trim(); // Remove whitespace
		filterValue = filterValue.toLowerCase(); // Datasource defaults to lowercase matches
		this.dataSource.filter = filterValue;
	}

	/** Whether the number of selected elements matches the total number of ros. */
	isAllSelected() {
		const numSelected = this.selection.selected?.length;
		const numRows = this.dataSource.filteredData?.length;
		return numSelected === numRows;
	}

	/** Selects all rows if they are not all selected; otherwise clear selection. */
	masterToggle() {
		this.isAllSelected() ? this.selection.clear() : this.dataSource.filteredData.forEach((row) => this.selection.select(row));
	}

	reloadData() {
		this.dataSource = new MatTableDataSource();
		this.paginator = this.araPaginator().paginator();
		if (this.model.dateSelect === undefined) {
			return;
		}
		this.selection.clear();
		this.finishedLoading = false;
		const consumptionRequest: ConsumptionRequest = {};
		consumptionRequest.readingDate = this.model.dateSelect;
		this.loadDataForPM();
	}

	printAction() {
		if (this.uvisInQueue() <= 0) {
			return this.showPrintDialog();
		}
		const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
			disableClose: true,
			autoFocus: false,
			restoreFocus: false,
		});
		dialogRef.componentInstance.titleMessage = this.translate('common.attention');
		dialogRef.componentInstance.confirmMessage = this.translate('massPrint.uviCreationStillInProgress');
		dialogRef
			.afterClosed()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (result) => {
					if (result) {
						this.showPrintDialog();
					}
				},
			});
	}

	showPrintDialog() {
		if (this.selection.selected?.length === 0) {
			this.snackBar.error(this.translocoService.translate('massPrint.missingSelection'));
			return;
		}
		const printSettingsDialog: any = {};
		const dialogRef = this.dialog.open(PrintDialogComponent, {
			disableClose: true,
			data: printSettingsDialog,
			maxWidth: '90%',
			minWidth: '600px',
		});
		dialogRef
			.afterClosed()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (result) => {
					this.finishedLoading = false;
					if (!result.saved) {
						this.finishedLoading = true;
						return;
					}
					if (result.data.targetFormat === 'DOCUMENT') {
						return this.getReportsAsync(result.data.printProtocol, result.data.fileFormat === 'PDF');
					} else {
						this.performTransfer(result, result.data.printProtocol);
					}
				},
			});
	}

	setFilterState(state: boolean) {
		if (!this.filtersSet) {
			this.filtersSet = state;
		}
	}

	switchAggregatedOrSingle($event: MatSlideToggleChange) {
		if ($event.checked) {
			this.showAggregated = true;
			this.changeColumnVisibilityIfAggregated(false, true);
			if (this.model.printOption && this.model.dateSelect && this.model.manualDeliveryOption) {
				this.reloadData();
			}
		} else {
			this.showAggregated = false;
			this.changeColumnVisibilityIfAggregated(true, false);
			if (this.model.printOption && this.model.dateSelect && this.model.manualDeliveryOption) {
				this.reloadData();
			}
		}
	}

	setFileName(): string {
		const activeLang = this.translocoService.getActiveLang();
		const cIY = activeLang === 'en' ? 'cIY' : 'uVI';
		const month = '_' + dayjs(this.model.selectedDate).locale(activeLang).format('MMMM');
		const year = '_' + dayjs(this.model.selectedDate).year();
		return cIY + month + year + '.pdf';
	}

	private createSortingDataAccessor() {
		return (realEstate, property) => {
			switch (property) {
				case 'addressBeautified':
					return realEstate.addressBeautified?.street + realEstate.addressBeautified?.city;
				default:
					return realEstate[property];
			}
		};
	}

	private setBreadcrumb() {
		this.breadCrumbService.reset();
		this.breadCrumbService.addBreadCrumb({
			translationKey: 'sideboard.massPrint',
			icon: this.icons.get('print'),
		});
	}

	private parseToAddressDataSource(address: Address): AddressDatasource {
		let streetString = '';
		if (!address || !address.streets) {
			return {
				street: '',
				zipCode: '',
				city: '',
			};
		}
		for (const street of address.streets) {
			if (streetString !== '') {
				streetString += ', ';
			}
			streetString += street.streetName + ' ' + street.houseNumber;
		}
		return {
			street: streetString,
			zipCode: address.zipCode,
			city: address.city,
		};
	}

	private performTransfer(result, billable: boolean) {
		if (result.saved) {
			const tenantReports = this.collectReportsToPrint(result.data);
			this.snackBar.success(this.translocoService.translate('jobLog.started'));
			this.jobStateService.openDialog();
			this.jobStateService.setAllJobsFinished(false);
			this.jobStateService.setJobStatusPolled(this.jobLogService, this.sessionService);
			return this.eedPrintController
				.transferReportsToOutboxAsync(billable, tenantReports)
				.pipe(
					finalize(() => {
						this.finishedLoading = true;
					}),
					takeUntilDestroyed(this.destroyRef)
				)
				.subscribe({
					next: () => {
						this.snackBar.success(this.translocoService.translate('massPrint.successfullyTransferredToOutbox'));
					},
				});
		}
	}

	private collectReportsToPrint(appendixFlag: any) {
		if (this.sort().active) {
			this.selection.sort((a, b) => {
				return this.sortByProperty(a, b, this.sort().active, this.sort().direction);
			});
		}
		const realEstateReports: RealEstateReport[] = [];

		this.selection.selected.forEach((realEstate) => {
			const realEstateReport = this.createRealEstateReport(realEstate);

			realEstateReport.appendixSendingOptions = appendixFlag.sheetOption;
			realEstateReports.push(realEstateReport);
		});

		return realEstateReports;
	}

	private createRealEstateReport(realEstate: RealEstateDatasource) {
		const realEstateReport: RealEstateReport = {};
		realEstateReport.realEstateId = realEstate.realEstateId;
		realEstateReport.realEstateNumber = realEstate.number;
		realEstateReport.tenantUnitIds = [];

		realEstate.tenantUnits.forEach((value) => realEstateReport.tenantUnitIds.push(value));
		realEstateReport.area = realEstate.area;
		realEstateReport.dueDate = this.model.dateSelect;
		if (this.model.manualDeliveryOption === ManualDeliveryOptionEnum.DELIVERY_TO_PM) {
			realEstateReport.withCoverLetter = true;
		}

		realEstateReport.coverSheetId = '2';
		realEstateReport.propertyManagementAccountNumber = realEstate.propertyManagementAccountNumber;
		return realEstateReport;
	}

	private getReportsAsync(billable: boolean, asSinglePdf) {
		const realEstateReports = this.collectReportsToPrint({ sheetOption: 'NO' });
		this.snackBar.success(this.translocoService.translate('jobLog.started'));
		this.jobStateService.setAllJobsFinished(false);
		this.jobStateService.setJobStatusPolled(this.jobLogService, this.sessionService);
		this.jobStateService.openDialog();

		return this.eedPrintController
			.getReportsAsync(this.currentArea, billable, asSinglePdf, realEstateReports)
			.pipe(
				finalize(() => {
					this.finishedLoading = true;
				}),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe(() => {});
	}

	private async loadDataForPM() {
		const readingDate = dayjs(this.model.dateSelect).format('YYYY-MM-DD');

		// Die aggregierte Abfrage leitet die Hausverwaltung serverseitig ab und braucht keine Contact-Ids.
		if (this.showAggregated) {
			this.loadData(this.eedPrintController.getPrintableRealEstatesAndTUsForPM(this.currentArea, this.model.printOption, readingDate));
			return;
		}

		const propertyManagementIds = await this.resolvePropertyManagementIds();
		if (!propertyManagementIds.length) {
			this.dataSource = new MatTableDataSource();
			this.finishedLoading = true;
			this.snackBar.error(this.translocoService.translate('errors.massPrintNoPropertyManagementResolved'));
			return;
		}

		const getEedsForPMs = propertyManagementIds.map((id) =>
			this.eedPrintController.getPrintableEedsForPM(this.currentArea, id, this.model.printOption, readingDate)
		);
		this.loadData(zip(getEedsForPMs));
	}

	/** HV-Contact-Ids fuer die Einzelabfrage: bevorzugt aus den Kontakten, sonst aus den PROPERTY_MANAGEMENT-Berechtigungen. */
	private async resolvePropertyManagementIds(): Promise<string[]> {
		const contacts = await this.sessionService.getPropertyManagementContacts();
		const contactIds = (contacts ?? []).map((contact) => contact.id).filter((id) => !!id);
		if (contactIds.length) {
			return contactIds;
		}
		const permissions = await firstValueFrom(
			this.sessionService.getAllPermissionsOfLoggedInUser().pipe(
				catchError(() => of([])),
				defaultIfEmpty([])
			)
		);
		return (permissions ?? [])
			.filter((permission) => permission.permissionType === PermissionObject.PermissionTypeEnum.PROPERTY_MANAGEMENT)
			.map((permission) => permission.tenantUnitId)
			.filter((id) => !!id);
	}

	private loadData(getPrintableRealEstatesWithTenantUnits$: Observable<Array<any>>) {
		if (this.showAggregated) {
			getPrintableRealEstatesWithTenantUnits$
				.pipe(takeUntilDestroyed(this.destroyRef))
				.pipe(
					finalize(() => {
						this.finishedLoading = true;
					})
				)
				.subscribe({
					next: async (realEstatePrintStates: RealEstatePrintState[]) => {
						this.fillDataSource(realEstatePrintStates);
					},
					error: () => {
						this.snackBar.error(this.translocoService.translate('errors.massPrint'));
					},
				});
		} else {
			getPrintableRealEstatesWithTenantUnits$
				.pipe(takeUntilDestroyed(this.destroyRef))
				.pipe(
					finalize(() => {
						this.finishedLoading = true;
					})
				)
				.subscribe({
					next: (massPrintDatas: [MassPrintData[]]) => {
						let values: MassPrintData[] = [];
						massPrintDatas.forEach((mpd) => (values = values.concat(mpd)));
						this.dataSource = new MatTableDataSource(this.parseMassPrintDataToRealEstateDatasource(values));
						this.dataSource.sortingDataAccessor = this.createSortingDataAccessor();
						this.dataSource.sort = this.sort();
						this.dataSource.paginator = this.paginator;
						this.dataSource.filterPredicate = this.createFilterPredicate();
						this.loadUvisInQueue();
					},
					error: () => {
						this.snackBar.error(this.translocoService.translate('errors.massPrint'));
					},
				});
		}
	}

	private loadUvisInQueue() {
		this.eedPrintController.getEedCountFromQueue(this.currentArea, { readingDate: this.model.dateSelect }).subscribe((uvisInQueue) => {
			this.uvisInQueue.set(uvisInQueue);
		});
	}

	private fillDataSource(realEstatePrintStates: RealEstatePrintState[]) {
		this.dataSource = new MatTableDataSource(this.parseToRealEstateDatasource(realEstatePrintStates));
		this.dataSource.sortingDataAccessor = this.createSortingDataAccessor();
		this.dataSource.sort = this.sort();
		this.dataSource.paginator = this.paginator;
		this.dataSource.filterPredicate = this.createFilterPredicate();
		this.loadUvisInQueue();
	}

	private parseToRealEstateDatasource(realEstatePrintStates: RealEstatePrintState[]): RealEstateDatasource[] {
		const dataSource: RealEstateDatasource[] = [];
		for (const realEstatePrintState of realEstatePrintStates) {
			const realEstateDatasource = new RealEstateDatasource();
			realEstateDatasource.area = this.currentArea;
			realEstateDatasource.number = realEstatePrintState.realEstateNumber;
			realEstateDatasource.propertyManagementAccountNumber = realEstatePrintState.accountNumber;
			realEstateDatasource.address = realEstatePrintState.address;
			realEstateDatasource.selection = true;
			realEstateDatasource.realEstateId = realEstatePrintState.realEstateId;
			realEstateDatasource.propertyManagementNumber = realEstatePrintState.propertyManagementNumber;
			// realEstateDatasource.propertyManagementId = currentRealEstate.propertyManagementContactId;
			realEstateDatasource.tenantUnits = realEstatePrintState.eedTenantUnitPrintStates.map((value) => value.tenantUnitId);
			realEstateDatasource.addressBeautified = this.parseToAddressDataSource(realEstatePrintState.address);
			dataSource.push(realEstateDatasource);
		}
		return dataSource;
	}

	private createFilterPredicate() {
		return (realEstate, filter: string) => {
			const accumulator = (currentTerm, key) => {
				if (key === 'address') {
					return currentTerm + realEstate.address?.street + realEstate.address?.city + realEstate.address?.zipCode;
				} else if (key === 'addressBeautified') {
					return currentTerm + realEstate.addressBeautified.street + realEstate.addressBeautified.city + realEstate.addressBeautified.zipCode;
				} else if (key === 'propertyManagementCompany') {
					return (
						currentTerm + realEstate.propertyManagementCompany?.companyNames[0]?.name + realEstate.propertyManagementCompany?.companyNames[1]?.name
					);
				} else {
					return currentTerm + realEstate[key];
				}
			};
			let keysWithoutIds = Object.keys(realEstate);
			keysWithoutIds = keysWithoutIds.filter((arrayItem) => arrayItem !== 'id');
			const dataStr = keysWithoutIds.reduce(accumulator, '').toLowerCase();
			// Transform the filter by converting it to lowercase and removing whitespace.
			const transformedFilter = filter.trim().toLowerCase();
			return dataStr.indexOf(transformedFilter) !== -1;
		};
	}

	private sortByProperty(a: RealEstateDatasource, b: RealEstateDatasource, property: string, direction: string): number {
		if (direction === 'asc' || direction === '') {
			if (a[property] > b[property]) {
				return 1;
			} else if (a[property] < b[property]) {
				return -1;
			} else {
				return 0;
			}
		} else if (direction === 'desc') {
			if (a[property] < b[property]) {
				return 1;
			} else if (a[property] > b[property]) {
				return -1;
			} else {
				return 0;
			}
		}
		return 0;
	}

	private parseMassPrintDataToRealEstateDatasource(massPrintDatas: MassPrintData[]): RealEstateDatasource[] {
		const dataSource: RealEstateDatasource[] = [];
		for (const massPrintData of massPrintDatas) {
			const realEstateDatasource = new RealEstateDatasource();
			realEstateDatasource.area = this.currentArea;
			realEstateDatasource.number = massPrintData.realEstateNumber;
			realEstateDatasource.propertyManagementAccountNumber = massPrintData.accountNumber;
			realEstateDatasource.selection = true;
			realEstateDatasource.propertyManagementNumber = massPrintData.propertyManagementNumber;
			realEstateDatasource.tenantUnitNumber = massPrintData.tenantUnitNumber;
			realEstateDatasource.tenantUnits = [massPrintData.tenantUnitId];
			realEstateDatasource.addressBeautified = this.parseToAddressDataSource(massPrintData.address);
			dataSource.push(realEstateDatasource);
		}
		return dataSource;
	}

	private changeColumnVisibilityIfAggregated(downloadVisible: boolean, tenantUnitCounterVisible: boolean) {
		this.columns().forEach((currentColumn) => {
			if (currentColumn.property === 'download') {
				currentColumn.visible = downloadVisible;
			}
			if (currentColumn.property === 'tenantUnitCounter') {
				currentColumn.visible = tenantUnitCounterVisible;
			}
		});
	}
}

class RealEstateDatasource {
	realEstateId?: string;
	area?: number;
	number?: number;
	propertyManagementNumber?: string; // PropertyManagementNumber at ServiceCompany
	propertyManagementAccountNumber?: string;
	propertyManagementId?: string;
	tenantUnitNumber?: string;
	addressBeautified?: AddressDatasource;
	address?: Address;
	selection = true;
	tenantUnits?: string[] = [];
}

class AddressDatasource {
	street?: string;
	zipCode?: string;
	city?: string;
}
